// src/pages/Summary.jsx
import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Label } from '../components/common';
import ToggleButton from '../components/common/ToggleButtonV2';
import DeviceSelector from '../components/common/DeviceSelector';
import MainPageLayout from '../components/common/MainPageLayout';
import FlexibleDateSelectorV2 from '../components/common/FlexibleDateSelectorV2';
import ViewModeToggle from '../components/common/ViewModeToggle';
import Table from '../components/common/Table/Table';
import WeeklyCards from '../components/common/DrilldownCards/WeeklyCards';
import DailyCards from '../components/common/DrilldownCards/DailyCards';
import CardV3 from '../components/CardV3';
import UsageSeriesNivo from '../components/Charts/UsageSeriesNivo';
import { secondsToHHMMSS } from '../utils/summary';
import Spinner from '../components/common/Spinner';
import { addDaysUTC } from '../utils/dates';

import { getDeviceMetrics, getFacilityMetricsSummary } from '../services/apiServices';
import { useUsageLast7DaysVanilla } from '../hooks/useUsageLast7DaysMetrics';
import useDeviceSelector from '../hooks/useDeviceSelector';
import UsageHistoryTableView from '../components/common/UsageHistoryTableView';

import SkeletonMetrics from '../components/common/DrillDownCard/Skeleton/SkeletonMetrics';
import SkeletonBody from '../components/common/DrillDownCard/Skeleton/SkeletonBody';

import {
  deviceMetrics,
  deviceMetricsColors,
  timelapse,
  summaryTitle,
  deviceActivityTitle,
} from '../constants/deviceMetrics';

import { formatToWeeks, buildDayTicks, buildMonthTicks, suffixFor } from '../utils/summary';
import { formatByMode } from '../utils/dates';

import 'rsuite/dist/rsuite.min.css';

const BlockSkeleton = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <SkeletonMetrics />
    <div className="mt-4">
      <SkeletonBody />
    </div>
  </div>
);

const Summary = () => {
  const [mode, setMode] = useState('day'); // 'day' | 'week' | 'month'
  const [summaryMetric, setSummary] = useState({});
  const [metricSelected, setMetricSelected] = useState('device_activity');
  const [metrics, setMetrics] = useState([]);
  const [unitSelected, setUnitSelected] = useState(null);
  const [dateSelected, setDateSelected] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [showEventsWithTransit, setShowEventsWithTransit] = useState(true);
  const [openWeekId, setOpenWeekId] = useState(null);

  // Unit data
  const { selectedUnit, loading } = useDeviceSelector();

  const handleShowTransit = () => {
    setShowEventsWithTransit((prev) => !prev);
  };

  // sincroniza unitSelected cuando cambia selectedUnit o cambian loaders
  useEffect(() => {
    const hasAnyLoadingTrue = Object.values(loading || {}).some(Boolean);
    if (selectedUnit && !hasAnyLoadingTrue) {
      setUnitSelected(selectedUnit);
    }
  }, [selectedUnit, loading]);

  const getTodayISO = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const {
    data,
    loading: usageLoading,
    refetch,
  } = useUsageLast7DaysVanilla({
    unitId: unitSelected,
    showTransit: true,
    dateSelected,
    enabled: mode === 'week',
    mode,
  });

  useEffect(() => {
    if (!unitSelected) return;
    if (mode === 'week' || mode === 'month') {
      refetch();
    }
  }, [mode, unitSelected, refetch]);

  const dateLabel = formatByMode(mode, dateSelected);
  useEffect(() => {
    // vuelve al día/semana/mes actuales
    setDateSelected(format(new Date(), 'yyyy-MM-dd'));
    setOpenWeekId(null); // opcional: cierra el que estaba abierto
  }, [mode]);

  useEffect(() => {
    const getMetricsParams = () => ({
      groupBy: mode,
      date: dateSelected || getTodayISO(),
    });

    const getSummaryParams = () => ({
      groupBy: mode,
      metric: metricSelected,
      date: dateSelected || getTodayISO(),
      limit: 10,
    });

    const params = getSummaryParams();
    const metricParams = getMetricsParams();

    const fetchDeviceMetrics = async () => {
      const deviceMetricsRes = await getDeviceMetrics(unitSelected.esp32_mac_address, params);
      const summaryMetrics = await getFacilityMetricsSummary(
        unitSelected.esp32_mac_address,
        metricParams.date,
      );

      console.log(summaryMetrics.items);

      console.log(summaryMetrics, 'SUMMARY METRICS ');

      // Process summary metrics from API response
      // API returns: { items: [{ startDate, endDate, metrics: { metricName: { value, formatted, delta } } }] }
      let summaryArray = [];

      if (
        summaryMetrics?.items &&
        Array.isArray(summaryMetrics.items) &&
        summaryMetrics.items.length > 0
      ) {
        // Get the most recent item (last in array, or first if sorted differently)
        // For now, using the first item - adjust if you need the most recent by date
        const latestItem = summaryMetrics.items[summaryMetrics.items.length - 1];

        if (latestItem?.metrics && typeof latestItem.metrics === 'object') {
          // Convert metrics object to array format for cards
          summaryArray = Object.entries(latestItem.metrics).map(([metricName, metricData]) => ({
            name: metricName,
            value: metricData?.value ?? 0,
            formatted: metricData?.formatted ?? '',
            delta: metricData?.delta ?? 0,
          }));
        }
      } else if (
        summaryMetrics?.items &&
        typeof summaryMetrics.items === 'object' &&
        !Array.isArray(summaryMetrics.items)
      ) {
        // Fallback: if items is a direct object (old format)
        summaryArray = Object.entries(summaryMetrics.items).map(([key, value]) => ({
          name: key,
          ...(typeof value === 'object' && value !== null ? value : {}),
        }));
      }

      let deviceMetricsFormatted = [];

      if (mode === 'week') {
        deviceMetricsFormatted = formatToWeeks(deviceMetricsRes.data, metricSelected);
      }
      if (mode === 'day') {
        deviceMetricsFormatted = buildDayTicks(deviceMetricsRes.data, metricSelected);
      }
      if (mode === 'month') {
        deviceMetricsFormatted = buildMonthTicks(deviceMetricsRes.data, metricSelected);
      }

      setSummary(summaryArray);
      setMetrics(deviceMetricsFormatted);
    };

    if (unitSelected) {
      fetchDeviceMetrics();
    }
  }, [mode, metricSelected, unitSelected, dateSelected]);

  const isTimeMetric = suffixFor(metricSelected) === 'h';

  const onChangeDateSelector = (value) => {
    setDateSelected(value);
  };

  const handleClickCard = (metric) => {
    setMetricSelected(metric);
  };

  const yMode = metricSelected === 'water_changes_count' ? 'number' : 'time';

  const showMetricst =
    (Array.isArray(summaryMetric) && summaryMetric.length > 0) ||
    Object.keys(summaryMetric || {}).length > 0;

  return (
    <MainPageLayout customHeaderProps={{ hideBackButton: true }}>
      <h4 className="text-center mb-4">{selectedUnit?.data_unit_name}</h4>

      <div className="max-w-[1440px] flex-1 flex flex-col">
        <div className="flex-1 flex  justify-center mb-6">
          <div className="flex flex-col items-center text-center">
            <div className="flex flex-wrap items-center justify-center gap-6 mb-[25px]">
              <DeviceSelector showSearchButton />
            </div>
          </div>
        </div>
      </div>

      {showMetricst ? (
        <>
          <div className="bg-gray-50 p-6 rounded-xl grid grid-cols-1 gap-6">
            {/* Line Chart */}
            <UsageSeriesNivo
              title={`${deviceMetrics[metricSelected]} - ${timelapse[mode]}`}
              data={metrics}
              color={deviceMetricsColors[metricSelected]}
              valueSuffix={isTimeMetric ? 'h' : ''}
              granularity={mode}
              yFormatter={(h) => secondsToHHMMSS(h, { showSeconds: false })}
              tooltipFormatter={(h) => secondsToHHMMSS(h, { showSeconds: true })}
              mode={mode}
              yMode={yMode}
            />

            <div>
              {/* Filtros */}
              <div className="flex flex-wrap items-center w-full mt-[30px] mb-[40px] gap-x-6 gap-y-8">
                <div className="flex items-center gap-3">
                  <p className="mr-5 text-slate-500">Showing details for:</p>
                  <FlexibleDateSelectorV2
                    onChange={onChangeDateSelector}
                    mode={mode}
                    key={`fds-${mode}`}
                  />
                </div>
                <div className="min-w-[200px] md:ml-auto">
                  <ViewModeToggle
                    label="View by:"
                    options={[
                      { label: 'Day', value: 'day' },
                      { label: 'Week', value: 'week' },
                      { label: 'Month', value: 'month' },
                    ]}
                    value={mode}
                    onChange={setMode}
                  />
                </div>
              </div>

              <h4 className="mb-4">
                {summaryTitle[mode]} - {dateLabel}
              </h4>

              {/* Metric Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {summaryMetric &&
                  Array.isArray(summaryMetric) &&
                  summaryMetric.map((metric) => (
                    <CardV3
                      key={metric?.name || metric?.title}
                      {...metric}
                      handleClickCard={handleClickCard}
                      mode={mode}
                      color={deviceMetricsColors[metricSelected]}
                      metricSelected={metricSelected}
                    />
                  ))}
              </div>
            </div>
          </div>

          {/* Drilldown */}
          <div className="bg-gray-50 p-6 rounded-xl grid grid-cols-1 gap-6 mt-10">
            <div className="space-y-6">
              {usageLoading ? (
                <>
                  {mode === 'week' &&
                    Array.from({ length: 7 }).map((_, i) => <BlockSkeleton key={`wk-skel-${i}`} />)}
                  {mode === 'month' &&
                    Array.from({ length: 4 }).map((_, i) => <BlockSkeleton key={`mo-skel-${i}`} />)}
                </>
              ) : (
                <>
                  {mode === 'week' && data && (
                    <>
                      <h4>
                        {deviceActivityTitle[mode]} - {dateLabel}
                      </h4>
                      {data.map((d) => (
                        <DailyCards key={d?.date || d?.value || d?.label} dailyItems={d} />
                      ))}
                    </>
                  )}

                  {mode === 'day' && (
                    <>
                      <h4 className="text-center">Activity Breakdown - {dateLabel}</h4>
                      <div className="toggle-container flex sm:justify-end my-6">
                        <Label variant="gray">Show Transits</Label>
                        <ToggleButton status={showEventsWithTransit} onToggle={handleShowTransit} />
                      </div>
                      <UsageHistoryTableView
                        filterParams={{
                          show_transit: showEventsWithTransit,
                          start_date: dateSelected,
                          end_date: addDaysUTC(dateSelected),
                        }}
                        areFiltersActive={false}
                        recordsPerPage={10}
                      >
                        {({ currentRecords, columns }) => (
                          <Table columns={columns} data={currentRecords} />
                        )}
                      </UsageHistoryTableView>
                    </>
                  )}

                  {mode === 'month' && data && (
                    <>
                      <h4>
                        {deviceActivityTitle[mode]} - {dateLabel}
                      </h4>
                      {data.map((w) => {
                        const id = w?.value || w?.table; // value (inicio de semana) es ideal
                        const isOpen = openWeekId === id;
                        return (
                          <WeeklyCards
                            key={id}
                            weeklyMetrics={w}
                            isOpen={isOpen}
                            onToggle={() => setOpenWeekId((prev) => (prev === id ? null : id))}
                          />
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      ) : (
        <>{unitSelected && <Spinner />}</>
      )}
    </MainPageLayout>
  );
};

export default Summary;
